import { useMemo, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { COUNTRY_OPTIONS, countryName } from "@/lib/utils/countries";

type CountryMultiSelectProps = {
  id?: string;
  /** Uppercase ISO 3166 alpha-2 codes. */
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  invalid?: boolean;
};

/** Searchable multi-select of countries, storing ISO alpha-2 codes. */
export function CountryMultiSelect({
  id,
  value,
  onChange,
  disabled,
  invalid,
}: CountryMultiSelectProps) {
  const [query, setQuery] = useState("");
  const selected = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q
    );
  }, [query]);

  function toggle(code: string) {
    if (selected.has(code)) onChange(value.filter((c) => c !== code));
    else onChange([...value, code].sort());
  }

  const summary =
    value.length === 0
      ? "Select countries"
      : `${value.length} ${value.length === 1 ? "country" : "countries"} selected`;

  return (
    <div className="space-y-2">
      <Popover onOpenChange={(open) => !open && setQuery("")}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className="border-input focus-visible:ring-ring/50 aria-invalid:border-destructive flex h-8 w-full cursor-pointer items-center justify-between gap-2 rounded-md border bg-transparent px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={value.length === 0 ? "text-muted-foreground" : undefined}>
              {summary}
            </span>
            <ChevronDown className="text-muted-foreground size-4 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2">
          <div className="relative mb-2">
            <Search className="text-muted-foreground absolute left-2 top-1/2 size-3.5 -translate-y-1/2" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search countries"
              className="h-8 pl-7 text-sm"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-muted-foreground px-1 py-2 text-xs">No countries found.</p>
            ) : (
              filtered.map((c) => (
                <label
                  key={c.code}
                  className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm"
                >
                  <Checkbox checked={selected.has(c.code)} onCheckedChange={() => toggle(c.code)} />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground text-xs">{c.code}</span>
                </label>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((code) => (
            <Badge key={code} variant="secondary" className="gap-1 font-normal">
              {countryName(code)} ({code})
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${countryName(code)}`}
                  onClick={() => toggle(code)}
                  className="hover:text-foreground text-muted-foreground cursor-pointer"
                >
                  <X className="size-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
